import { css, html, LitElement } from 'lit';

import { customElement, property } from 'lit/decorators.js';
import { sortBy } from 'lodash';
import '../../library/elements/d-action.js';
import type { OrganizationReference } from '@trinnvis/dabih-api-client';
import type { User } from '../../store/types.js';
import { getOrganizationUrl } from 'src/store/selectors';
import { sectorOptions } from 'src/store/selectors/features/sectorOptions';
import { t } from 'src/i18n';

/**
 *
 * Har ikke lenger funksjonalitet for valgt organisasjon. Alle her kan velges. Dette vises kun når ingen organisasjon er valgt
 *
 * Se også account. Denne vises selv om der kun er en organisasjon.
 *
 * Kanskje vi skal ta den vekk. Utenom hvis de har ingen organisasjon?
 *
 * The links are marked `rel="external"` so that they trigger a page reload.
 *
 */
@customElement('d-select-organization')
export class DSelectOrganization extends LitElement {
  static readonly styles = css`
    :host {
      display: block;
    }
    * {
      margin: 0;
      padding: 0;
      outline: 0;
      -webkit-text-size-adjust: 100%;
      -webkit-margin-before: 0;
      -webkit-margin-after: 0;
      -webkit-margin-start: 0;
      -webkit-margin-end: 0;
      -webkit-padding-start: 0;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    }

    #accountSelector {
      list-style-type: none;
      margin: 12px 0 12px 0;
      font-weight: 400;
    }

    #accountSelector a {
      display: block;
      margin-top: 12px;
      border-radius: 12px;
      background: hsl(195 65% 70%);
      padding: 9px 8px 8px 12px;
      color: black;
      text-decoration: none;
      cursor: pointer;
    }

    :host([outskirts]) #accountSelector a:not(.new) {
      background: hsl(196 84% 73%);
    }

    #accountSelector a[current] {
      background-color: white;
      cursor: default;
    }

    #accountSelector a.new {
      border: 3px dashed hsla(0, 0%, 100%, 0.5);
      background: transparent;
      color: white;
      font-size: 17px;
    }

    @media (hover: hover) {
      #accountSelector a:not(.new):not([current]):hover {
        color: black;
        background-color: hsla(0, 0%, 100%, 0.6);
      }
      #accountSelector a.new:hover {
        border: 3px solid transparent;
        background-color: hsla(0, 0%, 100%, 0.6);
        color: black;
      }
    }

    .header {
      margin-bottom: 4px;
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 0 4px;
    }

    .name {
      display: block;
      font-weight: 500;
      font-size: 17px;
    }

    .sector {
      display: block;
      font-weight: 400;
    }

    d-action {
      margin-top: 12px;
      margin-left: -8px;
      color: white;
    }
  `;
  @property({ type: Array })
  organizations: OrganizationReference[] = [];
  @property({ type: Number })
  organizationId = 0;
  @property({ type: Object })
  user!: User;
  @property({ type: Object })
  currentEmployee?: { firstName: string; lastName: string };

  get sortedOrganizations() {
    const a = this.organizations.slice();
    return sortBy(a, [(item) => (item.name ? item.name.toUpperCase() : '')]);
  }

  private sector(value: string): string {
    const s = sectorOptions.find((o) => {
      return o.value === value;
    });
    if (s) {
      return s.text;
    }
    return '';
  }

  render() {
    return html`
      <div id="accountSelector">
        ${this.sortedOrganizations.map(
          (organization) => html`
            <a
              href="${getOrganizationUrl(organization)}"
              ?current=${organization.id === this.organizationId}
              @click=${this.fireCloseSettings}
            >
              <span class="header">
                <span class="name"
                  >${organization.name} ${organization.closed ? t('meOrganizations.closedSuffixLower') : ''}</span
                >
                <span class="sector">${this.sector(organization.sector)}</span>
              </span>
              <span class="owner-email">${organization.ownerName} ${organization.ownerEmail}</span>
            </a>
          `,
        )}
        <a class="new" href="/signup">${t('account.meOrganizations.addNewOrganization')}</a>
      </div>
    `;
  }

  private fireCloseSettings() {
    this.dispatchEvent(new CustomEvent('close-settings', { bubbles: true, composed: true }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-select-organization': DSelectOrganization;
  }
}
